import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Package, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
    { id: "login", label: "Entrar", icon: LogIn, testid: "auth-mode-login" },
    { id: "register", label: "Criar conta", icon: UserPlus, testid: "auth-mode-register" },
];

export const AuthForm = () => {
    const { setUser, login } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [busy, setBusy] = useState(false);
    // fluxo esqueci a senha
    const [resetStep, setResetStep] = useState(0); // 0: inativo, 1: pediu código, 2: digitar código
    const [reset, setReset] = useState({ email: "", code: "", newPassword: "" });
    const pendingCheckout = localStorage.getItem("zf_pending_checkout") === "1";

    const afterAuth = (user) => {
        setUser(user);
        toast.success(`Bem-vindo, ${(user.name || user.email).split(" ")[0]}!`);
        const pending = localStorage.getItem("zf_pending_checkout") === "1";
        localStorage.removeItem("zf_pending_checkout");
        navigate(pending ? "/carrinho" : "/perfil", { replace: true });
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const url = mode === "login" ? "/auth/login" : "/auth/register";
            const payload =
                mode === "login"
                    ? { email: form.email, password: form.password }
                    : { name: form.name, email: form.email, password: form.password };
            const { data } = await api.post(url, payload);
            afterAuth(data.user);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const sendCode = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email: reset.email });
            toast.info(data.message || "Se o e-mail estiver cadastrado, você receberá um código.");
            setResetStep(2);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const resetPassword = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/reset-password", {
                email: reset.email,
                code: reset.code,
                new_password: reset.newPassword,
            });
            toast.success("Senha atualizada! Você já está dentro.");
            setReset({ email: "", code: "", newPassword: "" });
            setResetStep(0);
            setMode("login");
            afterAuth(data.user);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "border-forge-line bg-forge-ink focus-visible:ring-forge-yellow";
    const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-forge-dim";

    if (resetStep > 0) {
        return (
            <div
                className="layer-lines mx-auto flex max-w-lg flex-col gap-6 border border-forge-line bg-forge-panel p-8 md:p-10"
                data-testid="reset-flow"
            >
                <div className="text-center">
                    <Package className="mx-auto h-10 w-10 text-forge-yellow" />
                    <h1 className="mt-3 font-display text-2xl font-bold uppercase text-forge-paper">
                        {resetStep === 1 ? "Esqueceu a senha" : "Digite o código"}
                    </h1>
                    <p className="mt-2 text-sm leading-relaxed text-forge-dim">
                        {resetStep === 1
                            ? "Informe seu e-mail e enviaremos um código para criar uma nova senha. O código só é enviado para e-mails que já têm conta criada aqui — se você nunca criou conta, use a aba \u201CCriar conta\u201D (quem entrou com Google redefine a senha pelo próprio Google)."
                            : `Enviamos um código de 6 dígitos para ${reset.email}. Ele expira em 15 minutos.`}
                    </p>
                </div>

                {resetStep === 1 ? (
                    <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-form">
                        <div className="space-y-2">
                            <Label htmlFor="forgot-email" className={labelCls}>E-mail</Label>
                            <Input
                                id="forgot-email"
                                type="email"
                                data-testid="forgot-email-input"
                                value={reset.email}
                                onChange={(e) => setReset({ ...reset, email: e.target.value })}
                                required
                                placeholder="voce@email.com"
                                className={inputCls}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="forgot-send-button"
                            className="w-full bg-forge-yellow py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {busy ? "Enviando..." : "Enviar código"}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={resetPassword} className="space-y-4" data-testid="reset-form">
                        <div className="space-y-2">
                            <Label htmlFor="reset-code" className={labelCls}>Código</Label>
                            <Input
                                id="reset-code"
                                inputMode="numeric"
                                maxLength={6}
                                data-testid="reset-code-input"
                                value={reset.code}
                                onChange={(e) => setReset({ ...reset, code: e.target.value.replace(/\D/g, "") })}
                                required
                                placeholder="000000"
                                className={`${inputCls} text-center font-mono text-lg tracking-[0.5em]`}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="reset-password" className={labelCls}>Nova senha</Label>
                            <Input
                                id="reset-password"
                                type="password"
                                data-testid="reset-new-password-input"
                                value={reset.newPassword}
                                onChange={(e) => setReset({ ...reset, newPassword: e.target.value })}
                                required
                                minLength={8}
                                placeholder="Mínimo 8 caracteres"
                                className={inputCls}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="reset-submit-button"
                            className="w-full bg-forge-yellow py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {busy ? "Verificando..." : "Redefinir senha"}
                        </button>
                        <button
                            type="button"
                            onClick={() => setResetStep(1)}
                            data-testid="resend-code-link"
                            className="w-full text-center font-mono text-[10px] uppercase tracking-[0.2em] text-forge-dim underline underline-offset-4 hover:text-forge-yellow"
                        >
                            Não recebeu? Enviar de novo
                        </button>
                    </form>
                )}

                <button
                    type="button"
                    onClick={() => { setResetStep(0); setMode("login"); }}
                    data-testid="reset-back-link"
                    className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-forge-dim underline underline-offset-4 hover:text-forge-paper"
                >
                    Voltar para o login
                </button>
            </div>
        );
    }

    return (
        <div
            className="layer-lines mx-auto flex max-w-lg flex-col gap-6 border border-forge-line bg-forge-panel p-8 md:p-10"
            data-testid="perfil-auth-card"
        >
            <div className="text-center">
                <Package className="mx-auto h-10 w-10 text-forge-yellow" />
                <h1 className="mt-3 font-display text-2xl font-bold uppercase text-forge-paper">Sua conta</h1>
                <p className="mt-2 text-sm leading-relaxed text-forge-dim">
                    Entre para ver suas compras, carrinho, pedidos e favoritos.
                </p>
                {pendingCheckout && (
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-forge-yellow">
                        Entre para finalizar sua compra
                    </p>
                )}
            </div>

            <div className="flex gap-6 border-b border-forge-line" data-testid="auth-mode-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setMode(t.id)}
                        data-testid={t.testid}
                        className={`relative flex-1 pb-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                            mode === t.id ? "text-forge-yellow" : "text-forge-dim hover:text-forge-paper"
                        }`}
                    >
                        <span className="flex items-center justify-center gap-2">
                            <t.icon className="h-3.5 w-3.5" />
                            {t.label}
                        </span>
                        {mode === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-forge-yellow" />}
                    </button>
                ))}
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="auth-email-form">
                {mode === "register" && (
                    <div className="space-y-2">
                        <Label htmlFor="auth-name" className={labelCls}>Nome</Label>
                        <Input
                            id="auth-name"
                            data-testid="auth-name-input"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            minLength={2}
                            placeholder="Seu nome"
                            className={inputCls}
                        />
                    </div>
                )}
                <div className="space-y-2">
                    <Label htmlFor="auth-email" className={labelCls}>E-mail</Label>
                    <Input
                        id="auth-email"
                        type="email"
                        data-testid="auth-email-input"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        placeholder="voce@email.com"
                        className={inputCls}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="auth-password" className={labelCls}>Senha</Label>
                    <Input
                        id="auth-password"
                        type="password"
                        data-testid="auth-password-input"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                        minLength={mode === "register" ? 8 : undefined}
                        placeholder={mode === "register" ? "Mínimo 8 caracteres" : "••••••••"}
                        className={inputCls}
                    />
                    {mode === "login" && (
                        <button
                            type="button"
                            onClick={() => { setReset({ email: form.email, code: "", newPassword: "" }); setResetStep(1); }}
                            data-testid="forgot-link"
                            className="font-mono text-[10px] uppercase tracking-[0.15em] text-forge-dim underline underline-offset-4 transition-colors duration-300 hover:text-forge-yellow"
                        >
                            Esqueceu a senha?
                        </button>
                    )}
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="auth-submit-button"
                    className="w-full bg-forge-yellow py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                    {busy ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
                </button>
            </form>

            <div className="flex items-center gap-4">
                <span className="h-px flex-1 bg-forge-line" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">ou</span>
                <span className="h-px flex-1 bg-forge-line" />
            </div>

            <button
                type="button"
                onClick={login}
                data-testid="auth-google-button"
                className="w-full border border-forge-line py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow"
            >
                Continuar com Google
            </button>
        </div>
    );
};

export default AuthForm;
